'use client'

import { useEffect, useState } from 'react'

const CONSENT_KEY = 'wm_cookie_consent'
const GA_MEASUREMENT_ID = 'G-V6YVK9PG93'

type ConsentChoice = 'accepted' | 'declined'

export type ConsentTrackingMarkup = {
  bodyEnd: string
  bodyStart: string
  head: string
}

type CookieConsentProps = {
  trackingMarkup?: ConsentTrackingMarkup
}

const cloneWithExecutableScripts = (node: Node): Node => {
  if (node instanceof HTMLScriptElement) {
    const script = document.createElement('script')
    for (const attribute of Array.from(node.attributes)) {
      script.setAttribute(attribute.name, attribute.value)
    }
    script.textContent = node.textContent
    return script
  }

  const clone = node.cloneNode(false)
  for (const child of Array.from(node.childNodes)) {
    clone.appendChild(cloneWithExecutableScripts(child))
  }
  return clone
}

const injectMarkup = (
  markup: string,
  target: HTMLElement,
  placement: 'append' | 'prepend' = 'append',
) => {
  if (!markup.trim()) return [] as Node[]

  const template = document.createElement('template')
  template.innerHTML = markup
  const nodes = Array.from(template.content.childNodes).map(cloneWithExecutableScripts)

  if (placement === 'prepend') {
    for (const node of [...nodes].reverse()) {
      target.insertBefore(node, target.firstChild)
    }
  } else {
    for (const node of nodes) target.appendChild(node)
  }

  return nodes
}

export const CookieConsent = ({
  trackingMarkup = { head: '', bodyStart: '', bodyEnd: '' },
}: CookieConsentProps) => {
  const [mounted, setMounted] = useState(false)
  const [choice, setChoice] = useState<ConsentChoice | null>(null)

  useEffect(() => {
    const timer = window.setTimeout(() => {
      try {
        const stored = localStorage.getItem(CONSENT_KEY)
        if (stored === 'accepted' || stored === 'declined') {
          setChoice(stored)
        }
      } catch {
        setChoice(null)
      }
      setMounted(true)
    }, 0)

    return () => window.clearTimeout(timer)
  }, [])

  useEffect(() => {
    if (choice !== 'accepted') return

    const injectedNodes: Node[] = []
    const googleAnalytics = document.createElement('script')
    googleAnalytics.async = true
    googleAnalytics.src = `https://www.googletagmanager.com/gtag/js?id=${GA_MEASUREMENT_ID}`
    googleAnalytics.dataset.wmConsentTracking = 'google-analytics'
    document.head.appendChild(googleAnalytics)
    injectedNodes.push(googleAnalytics)

    const googleAnalyticsInit = document.createElement('script')
    googleAnalyticsInit.dataset.wmConsentTracking = 'google-analytics-init'
    googleAnalyticsInit.textContent = `
      window.dataLayer = window.dataLayer || [];
      function gtag(){dataLayer.push(arguments);}
      gtag('js', new Date());
      gtag('config', '${GA_MEASUREMENT_ID}');
    `
    document.head.appendChild(googleAnalyticsInit)
    injectedNodes.push(googleAnalyticsInit)

    injectedNodes.push(...injectMarkup(trackingMarkup.head, document.head))
    injectedNodes.push(...injectMarkup(trackingMarkup.bodyStart, document.body, 'prepend'))
    injectedNodes.push(...injectMarkup(trackingMarkup.bodyEnd, document.body))

    return () => {
      for (const node of injectedNodes) {
        if (node.parentNode) node.parentNode.removeChild(node)
      }
    }
  }, [choice, trackingMarkup.bodyEnd, trackingMarkup.bodyStart, trackingMarkup.head])

  const handleChoice = (nextChoice: ConsentChoice) => {
    try {
      localStorage.setItem(CONSENT_KEY, nextChoice)
      document.cookie = `${CONSENT_KEY}=${nextChoice}; path=/; max-age=31536000; SameSite=Lax; Secure`
    } catch {
      // The page remains usable even when browser storage is unavailable.
    }
    setChoice(nextChoice)
  }

  if (!mounted || choice) return null

  return (
    <aside className="cookie-banner" role="region" aria-label="Cookie consent notice">
      <div className="cookie-banner__inner">
        <div className="cookie-banner__content">
          <div className="cookie-banner__header">
            <span className="cookie-banner__icon" aria-hidden="true">
              🍪
            </span>
            <h2 className="cookie-banner__title">We Value Your Privacy</h2>
          </div>
          <p className="cookie-banner__text">
            We use cookies to enhance your experience, analyze traffic, and support site function.
            Learn more in our{' '}
            <a href="/cookie-policy" className="cookie-banner__link">
              Cookie Policy
            </a>{' '}
            and{' '}
            <a href="/privacy-policy" className="cookie-banner__link">
              Privacy Policy
            </a>
            .
          </p>
        </div>
        <div className="cookie-banner__actions">
          <button
            type="button"
            className="cookie-banner__btn cookie-banner__btn--decline"
            onClick={() => handleChoice('declined')}
          >
            Decline
          </button>
          <button
            type="button"
            className="cookie-banner__btn cookie-banner__btn--accept"
            onClick={() => handleChoice('accepted')}
          >
            Accept Cookies
          </button>
        </div>
      </div>
    </aside>
  )
}
